import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import maplibregl from 'maplibre-gl';
import { ArrowLeft, Check, Clock3, MapPin, ShieldCheck, UserCheck, Users, X } from 'lucide-react';
import { api } from '@/services/api';
import { PageHeader } from '@/components/ui/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { useToast } from '@/components/ui/toast';
import { RidePassQr } from './ride-pass-qr';

function meetingRadiusPolygon(center, radiusMeters, points = 48) {
  const latitudeScale = radiusMeters / 111320;
  const longitudeScale = radiusMeters / (111320 * Math.max(0.2, Math.cos(center[1] * Math.PI / 180)));
  const ring = Array.from({ length: points + 1 }, (_, index) => {
    const angle = index / points * Math.PI * 2;
    return [center[0] + Math.cos(angle) * longitudeScale, center[1] + Math.sin(angle) * latitudeScale];
  });
  return { type: 'Polygon', coordinates: [ring] };
}

function MeetingPointMap({ pass }) {
  const ref = useRef(null);
  useEffect(() => {
    const coordinates = pass?.meetingPoint?.coordinates;
    if (!ref.current || !Array.isArray(coordinates) || coordinates.length !== 2) return undefined;
    const checkedIn = (pass.redemptions || []).flatMap((item) => {
      const position = item.checkInPosition?.coordinates;
      if (!item.checkInAt || !Array.isArray(position) || position.length !== 2) return [];
      return [{
        type: 'Feature',
        geometry: { type: 'Point', coordinates: position },
        properties: { name: item.member?.name || 'Rider', source: item.source || 'qr' },
      }];
    });
    const instance = new maplibregl.Map({
      container: ref.current,
      style: {
        version: 8,
        glyphs: 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf',
        sources: { tiles: { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256 } },
        layers: [{ id: 'background', type: 'background', paint: { 'background-color': '#040810' } }, { id: 'tiles', type: 'raster', source: 'tiles', paint: { 'raster-saturation': -0.85, 'raster-brightness-max': 0.45 } }],
      },
      center: coordinates,
      zoom: 14,
      attributionControl: false,
    });
    instance.on('load', () => {
      instance.addSource('meeting-radius', {
        type: 'geojson',
        data: { type: 'Feature', geometry: meetingRadiusPolygon(coordinates, Number(pass.checkInRadiusMeters) || 300), properties: {} },
      });
      instance.addSource('meeting', { type: 'geojson', data: { type: 'Feature', geometry: { type: 'Point', coordinates }, properties: { label: pass.meetingPoint?.label || 'Meeting point' } } });
      instance.addSource('checked-in-riders', { type: 'geojson', data: { type: 'FeatureCollection', features: checkedIn } });
      instance.addLayer({ id: 'meeting-radius', type: 'fill', source: 'meeting-radius', paint: { 'fill-color': '#42e8e0', 'fill-opacity': 0.08, 'fill-outline-color': '#42e8e0' } });
      instance.addLayer({ id: 'meeting-halo', type: 'circle', source: 'meeting', paint: { 'circle-radius': 19, 'circle-color': '#42e8e0', 'circle-opacity': 0.13, 'circle-stroke-color': '#42e8e0', 'circle-stroke-width': 1.5 } });
      instance.addLayer({ id: 'meeting-core', type: 'circle', source: 'meeting', paint: { 'circle-radius': 8, 'circle-color': '#42e8e0', 'circle-stroke-color': '#f5f8fc', 'circle-stroke-width': 3 } });
      instance.addLayer({ id: 'checked-in-riders', type: 'circle', source: 'checked-in-riders', paint: { 'circle-radius': 6, 'circle-color': '#34d399', 'circle-stroke-color': '#061019', 'circle-stroke-width': 3 } });
      instance.addLayer({ id: 'checked-in-labels', type: 'symbol', source: 'checked-in-riders', minzoom: 13, layout: { 'text-field': ['get', 'name'], 'text-size': 10, 'text-offset': [0, 1.4] }, paint: { 'text-color': '#d1fae5', 'text-halo-color': '#061019', 'text-halo-width': 2 } });
      if (checkedIn.length) {
        const bounds = new maplibregl.LngLatBounds(coordinates, coordinates);
        checkedIn.forEach((feature) => bounds.extend(feature.geometry.coordinates));
        instance.fitBounds(bounds, { padding: 52, maxZoom: 15, duration: 0 });
      }
    });
    const resize = new ResizeObserver(() => instance.resize());
    resize.observe(ref.current);
    return () => { resize.disconnect(); instance.remove(); };
  }, [pass]);
  if (!pass?.meetingPoint?.coordinates) return <div className="invite-map-empty"><MapPin size={24}/><p>No meeting point configured.</p></div>;
  return <div ref={ref} className="invite-meeting-map"/>;
}

export function RidePassDetailPage() {
  const { passId } = useParams();
  const navigate = useNavigate();
  const client = useQueryClient();
  const toast = useToast();
  const query = useQuery({ queryKey: ['invite-hub', passId], queryFn: () => api(`/api/v1/admin/invite-hub/passes/${passId}`).then((result) => result.data), refetchInterval: 15_000 });
  const decide = useMutation({
    mutationFn: ({ redemptionId, decision }) => api(`/api/v1/admin/invite-hub/redemptions/${redemptionId}/decision`, { method: 'POST', body: JSON.stringify({ decision }) }),
    onSuccess: async () => { await client.invalidateQueries({ queryKey: ['invite-hub'] }); toast.show('Join request updated', 'success'); },
    onError: (cause) => toast.show(cause.message, 'error'),
  });
  const pass = query.data;
  if (query.isLoading) return <div className="p-8 text-sm text-slate-500">Loading Ride Pass...</div>;
  if (!pass) return <EmptyState title="Ride Pass not found"/>;
  return <div className="workspace-page">
    <PageHeader eyebrow="Invite Hub" title={pass.title} description={`${String(pass.inviteType).replaceAll('_', ' ')} · ${pass.code}`} actions={<Button variant="secondary" onClick={() => navigate('/invite-hub')}><ArrowLeft size={14}/>Back</Button>}/>
    <div className="ride-pass-detail-layout">
      <section className="ride-pass-command-card">
        <div className="flex items-start justify-between gap-3"><div><p className="eyebrow">Convoy Ride Pass</p><h2>{pass.title}</h2></div><Badge tone={pass.state === 'active' ? 'success' : 'warning'}>{pass.state}</Badge></div>
        <RidePassQr value={pass.joinUrl} title={pass.title} code={pass.code} compact/>
        <div className="ride-pass-code"><small>Manual code</small><strong>{pass.code}</strong></div>
        <div className="grid grid-cols-2 gap-2">
          <div className="ride-pass-stat"><Users size={14}/><span><small>Usage</small><strong>{pass.usageCount}/{pass.usageLimit}</strong></span></div>
          <div className="ride-pass-stat"><Clock3 size={14}/><span><small>Expires</small><strong>{new Date(pass.expiresAt).toLocaleString()}</strong></span></div>
          <div className="ride-pass-stat"><ShieldCheck size={14}/><span><small>Approval</small><strong>{pass.approvalRequired ? 'Required' : 'Instant'}</strong></span></div>
          <div className="ride-pass-stat"><UserCheck size={14}/><span><small>Checked in</small><strong>{pass.analytics?.checkedIn || 0}</strong></span></div>
        </div>
      </section>
      <section className="ride-pass-map-card"><div className="border-b border-white/8 p-4"><p className="eyebrow">Meet Point Mode</p><h2>Arrival map</h2></div><MeetingPointMap pass={pass}/><div className="grid grid-cols-3 border-t border-white/8 text-center text-[10px]"><span className="p-3 text-emerald-300">{pass.analytics?.checkedIn || 0}<small>Checked in</small></span><span className="border-x border-white/8 p-3 text-cyan-300">{pass.analytics?.accepted || 0}<small>Joined</small></span><span className="p-3 text-amber-300">{pass.analytics?.pending || 0}<small>Pending</small></span></div></section>
    </div>
    <section className="ride-pass-requests min-h-0 flex-1 overflow-hidden">
      <div className="flex items-center justify-between border-b border-white/8 p-4"><div><p className="eyebrow">Approval Center</p><h2>Join requests</h2></div><Badge tone="warning">{pass.analytics?.pending || 0} pending</Badge></div>
      <div className="scrollbar h-full overflow-y-auto divide-y divide-white/7">
        {(pass.redemptions || []).map((item) => <div key={item._id} className="ride-pass-request-row">
          <div className="ride-pass-request-avatar">{(item.member?.name || 'R').slice(0, 1).toUpperCase()}</div>
          <div className="min-w-0 flex-1"><strong>{item.member?.name || 'Rider'}</strong><p>{item.member?.email || item.member?.username || 'Google rider'} · {item.source}</p></div>
          <div className="text-right"><Badge tone={item.status === 'accepted' ? 'success' : item.status === 'pending' ? 'warning' : 'error'}>{item.status}</Badge>{item.checkInAt && <p className="mt-1 text-[9px] text-emerald-300">Checked in</p>}</div>
          {item.status === 'pending' && <div className="flex gap-2"><Button variant="secondary" className="h-9 px-3" onClick={() => decide.mutate({ redemptionId: item._id, decision: 'reject' })}><X size={14}/>Reject</Button><Button className="h-9 px-3" onClick={() => decide.mutate({ redemptionId: item._id, decision: 'approve' })}><Check size={14}/>Approve</Button></div>}
        </div>)}
        {!pass.redemptions?.length && <EmptyState title="No join requests" description="Scans and link opens will appear here."/>}
      </div>
    </section>
  </div>;
}
